import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenUtils';
import PayrollLayout from '../../layouts/PayrollLayout';
import Notification from "../../components/UI/Notification";
import DeleteButton from "../../components/UI/AlertDialog";
import { X } from "lucide-react";

// Constants
const API_URL = "https://hrmspayroll.prepstripe.com/api";

export default function Settings() {
  const [notification, setNotification] = useState(null);
  const token = getToken();
  
  // API instance
  const api = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  // State management
  const [settings, setSettings] = useState([]);
  const [editSettings, setEditSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSetting, setNewSetting] = useState({
    setting_key: '',
    setting_name: '',
    setting_value: '',
    data_type: 'string',
    description: '',
    category: 'general',
    is_editable: true
  });

  // Utility functions
  const toBool = useCallback((value) => {
    return value === true || value === 'true' || value === 1 || value === '1';
  }, []);

  const toNumber = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    return isNaN(num) ? '' : num;
  }, []);

  const getSettingValue = useCallback((key, settingsArray = editSettings) => {
    const setting = settingsArray.find(s => s.setting_key === key);
    return setting ? setting.setting_value : '';
  }, [editSettings]);

  const updateSettingValue = useCallback((key, value) => {
    setEditSettings(prev => 
      prev.map(setting => 
        setting.setting_key === key 
          ? { ...setting, setting_value: value }
          : setting
      )
    );
  }, []);

  // Group settings by category
  const settingsByCategory = useMemo(() => {
    const grouped = {};
    editSettings.forEach(setting => {
      if (!grouped[setting.category]) {
        grouped[setting.category] = [];
      }
      grouped[setting.category].push(setting);
    });
    return grouped;
  }, [editSettings]);

  // Get available categories
  const availableCategories = useMemo(() => {
    return [...new Set(settings.map(s => s.category))];
  }, [settings]);

  // Filter settings by selected category
  const filteredSettings = useMemo(() => {
    if (selectedCategory === 'all') return editSettings;
    return editSettings.filter(setting => setting.category === selectedCategory);
  }, [editSettings, selectedCategory]);

  // Check for changes
  useEffect(() => {
    const hasChanges = JSON.stringify(settings) !== JSON.stringify(editSettings);
    setHasChanges(hasChanges);
  }, [settings, editSettings]);

  // Create new setting function
  const createSetting = useCallback(async (settingData) => {
    try {
      const createPayload = {
        settings: [settingData]
      };

      const response = await api.post('/payroll/settings', createPayload);
      const createdSettings = response.data?.data || [];

      if (createdSettings.length > 0) {
        // Add new settings to both arrays
        setSettings(prev => [...createdSettings, ...prev]);
        setEditSettings(prev => [...createdSettings, ...prev]);
        
        setNotification({
          message: 'Setting created successfully',
          type: "success"
        });
        
        return createdSettings[0];
      }
    } catch (err) {
      setNotification({
        message: err.response?.data?.message || 'Failed to create setting',
        type: "error"
      });
      throw err;
    }
  }, [api]);

  // API functions
  const loadSettings = useCallback(async (filters = {}) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get('/payroll/settings', { params: filters });
      const settingsData = response.data?.data || [];
      
      setSettings([...settingsData]);
      setEditSettings([...settingsData]);
    } catch (err) {
      setNotification({
        message: err.response?.data?.message || 'Failed to load payroll settings',
        type: "error"
      });
      console.error('Load settings error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load payroll settings';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadSettingsByCategory = useCallback(async (category) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get(`/payroll/settings/category/${category}`);
      const settingsData = response.data?.data || [];
      
      // Update only settings for this category
      setSettings(prev => {
        const filtered = prev.filter(s => s.category !== category);
        return [...filtered, ...settingsData];
      });
      
      setEditSettings(prev => {
        const filtered = prev.filter(s => s.category !== category);
        return [...filtered, ...settingsData];
      });
    } catch (err) {
      setNotification({
        message: err.response?.data?.message || `Failed to load ${category} settings`,
        type: "error"
      });
      console.error('Load category settings error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Updated save function using bulk update endpoint
  const saveSettings = useCallback(async () => {
    setSaving(true);
    setError('');
    
    try {
      // Only send settings that have changed and are editable
      const changedSettings = editSettings.filter(editSetting => {
        const originalSetting = settings.find(s => s.setting_key === editSetting.setting_key);
        return originalSetting && 
               originalSetting.setting_value !== editSetting.setting_value &&
               editSetting.is_editable;
      });

      if (changedSettings.length === 0) {
        setNotification({
          message: 'No changes to save',
          type: "success"
        });
        return;
      }

      // Use bulk update endpoint
      const bulkUpdatePayload = {
        settings: changedSettings.map(setting => ({
          id: setting.id,
          setting_value: setting.setting_value,
          setting_name: setting.setting_name,
          description: setting.description
        }))
      };
      
      const response = await api.post('/payroll/settings-bulk-update', bulkUpdatePayload);
      const updatedSettings = response.data?.data || editSettings;
      
      // Update the original settings with the response
      setSettings(prev => {
        const updated = [...prev];
        updatedSettings.forEach(updatedSetting => {
          const index = updated.findIndex(s => s.id === updatedSetting.id);
          if (index !== -1) {
            updated[index] = updatedSetting;
          }
        });
        return updated;
      });
      
      setEditSettings(prev => {
        const updated = [...prev];
        updatedSettings.forEach(updatedSetting => {
          const index = updated.findIndex(s => s.id === updatedSetting.id);
          if (index !== -1) {
            updated[index] = updatedSetting;
          }
        });
        return updated;
      });
      
      setNotification({
        message: 'Settings updated successfully',
        type: "success"
      });
    } catch (err) {
      console.error('Save settings error:', err);
    
      let errorMessage = 'Failed to update payroll settings';
    
      const errorData = err.response?.data?.message || err.response?.data?.error;
    
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (typeof errorData === 'object' && errorData !== null) {
        errorMessage = Object.values(errorData)
          .flat() 
          .join(', ');
      }
    
      setNotification({
        message: errorMessage,
        type: "error"
      });
      setError(errorMessage);
    
      console.error('Detailed validation errors:', errorData);
    
    } finally {
      setSaving(false);
    }
  }, [api, editSettings, settings]);

  // Individual setting update function
  const updateIndividualSetting = useCallback(async (settingId, newValue) => {
    try {
      const setting = editSettings.find(s => s.id === settingId);
      if (!setting) return;

      const updatePayload = {
        setting_value: newValue,
        setting_name: setting.setting_name,
        description: setting.description
      };

      const response = await api.patch(`/payroll/settings/${settingId}`, updatePayload);
      const updatedSetting = response.data?.data;

      if (updatedSetting) {
        // Update both original and edit settings
        setSettings(prev => prev.map(s => s.id === settingId ? updatedSetting : s));
        setEditSettings(prev => prev.map(s => s.id === settingId ? updatedSetting : s));
        
        setNotification({
          message: `${setting.setting_name} updated successfully`,
          type: "success"
        });
      }
    } catch (err) {
      setNotification({
        message: err.response?.data?.message || 'Failed to update setting',
        type: "error"
      });
    }
  }, [api, editSettings]);

  // Delete setting function
  const deleteSetting = useCallback(async (settingId) => {

    try {
      await api.delete(`/payroll/settings/${settingId}`);
      
      // Remove from both settings arrays
      setSettings(prev => prev.filter(s => s.id !== settingId));
      setEditSettings(prev => prev.filter(s => s.id !== settingId));
      
      setNotification({
        message: 'Setting deleted successfully',
        type: "success"
      });
    } catch (err) {
      setNotification({
        message: err.response?.data?.message || 'Failed to delete setting',
        type: "error"
      });
    }
  }, [api]);

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Event handlers
  const handleInputChange = useCallback((settingKey, value) => {
    updateSettingValue(settingKey, value);
  }, [updateSettingValue]);

  const handleToggleChange = useCallback((settingKey) => {
    const currentValue = getSettingValue(settingKey);
    const newValue = !toBool(currentValue);
    updateSettingValue(settingKey, newValue);
  }, [getSettingValue, toBool, updateSettingValue]);

  const handleReset = useCallback(() => {
    setEditSettings([...settings]);
    setError('');
  }, [settings]);

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    if (category !== 'all') {
      loadSettingsByCategory(category);
    }
  }, [loadSettingsByCategory]);

  // Handle create setting form
  const handleCreateSetting = useCallback(async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!newSetting.setting_key || !newSetting.setting_name) {
      setNotification({
        message: 'Setting key and name are required',
        type: "error"
      });
      return;
    }

    // Check if setting key already exists
    const keyExists = settings.some(s => s.setting_key === newSetting.setting_key);
    if (keyExists) {
      setNotification({
        message: 'Setting key already exists',
        type: "error"
      });
      return;
    }

    try {
      await createSetting(newSetting);
      setShowCreateModal(false);
      setNewSetting({
        setting_key: '',
        setting_name: '',
        setting_value: '',
        data_type: 'string',
        description: '',
        category: 'general',
        is_editable: true
      });
    } catch (err) {
      // Error handling is done in createSetting function
    }
  }, [newSetting, settings, createSetting]);

  const handleNewSettingChange = useCallback((field, value) => {
    setNewSetting(prev => ({ ...prev, [field]: value }));
  }, []);

  // Render field based on data type
  const renderField = useCallback((setting) => {
    const value = getSettingValue(setting.setting_key);
    
    if (!setting.is_editable) {
      return (
        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-400">
          {value || 'Not set'}
        </div>
      );
    }

    switch (setting.data_type) {
      case 'boolean':
        return (
          <div className="flex items-center justify-between p-4 border dark:border-gray-700 rounded-lg">
            <div className="flex-1">
              <span className="font-medium dark:text-white">{setting.setting_name}</span>
              {setting.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {setting.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={toBool(value)}
                onChange={() => handleToggleChange(setting.setting_key)}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={saving}
              />
              <DeleteButton
                style={`text-red-600 hover:text-red-800 px-3 py-2`}
                onDelete={() => deleteSetting(setting.id)}
              />
            </div>
          </div>
        );

      case 'number':
        return (
          <div>
            <div className="flex justify-between items-start mb-2">
              <label className="block font-medium dark:text-white text-sm">
                {setting.setting_name}
              </label>
            </div>
            {setting.description && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                {setting.description}
              </p>
            )}
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                value={toNumber(value)}
                onChange={(e) => handleInputChange(setting.setting_key, e.target.value)}
                className="border dark:border-gray-700 dark:bg-gray-800 dark:text-white px-3 py-2 rounded-lg flex-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={saving}
              />
              <button
                onClick={() => updateIndividualSetting(setting.id, getSettingValue(setting.setting_key))}
                className="px-3 py-2 bg-blue-100 text-blue-700 border border-blue-600 font-semibold rounded-lg text-sm hover:bg-blue-200"
                disabled={saving}
                title='Save'>
                Save
              </button>
              <DeleteButton
                style={`text-red-600 hover:text-red-800 px-3 py-2`}
                onDelete={() => deleteSetting(setting.id)}
              />
            </div>
          </div>
        );

      case 'string':
      default:
        // Special handling for certain fields
        if (setting.setting_key === 'salary_cycle') {
          return (
            <div>
              <div className="flex justify-between items-start mb-2">
                <label className="block font-medium dark:text-white text-sm">
                  {setting.setting_name}
                </label>
              </div>
              {setting.description && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  {setting.description}
                </p>
              )}
              <div className='flex items-center gap-2'>
                <select
                  value={value}
                  onChange={(e) => handleInputChange(setting.setting_key, e.target.value)}
                  className="border dark:border-gray-700 dark:bg-gray-800 dark:text-white px-3 py-2 rounded-lg w-full text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={saving}>
                  <option value="monthly">Monthly</option>
                  <option value="bi-weekly">Bi-weekly</option>
                  <option value="weekly">Weekly</option>
                </select>
                <DeleteButton
                  style={`text-red-600 hover:text-red-800 px-3 py-2`}
                  onDelete={() => deleteSetting(setting.id)}
                />
              </div>
            </div>
          );
        }

        // Default text input
        return (
          <div>
            <div className="flex justify-between items-start mb-2">
              <label className="block font-medium dark:text-white text-sm">
                {setting.setting_name}
              </label>
            </div>
            {setting.description && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                {setting.description}
              </p>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(setting.setting_key, e.target.value)}
                className="border dark:border-gray-700 dark:bg-gray-800 dark:text-white px-3 py-2 rounded-lg flex-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={saving}
              />
              <button
                onClick={() => updateIndividualSetting(setting.id, getSettingValue(setting.setting_key))}
                className="px-3 py-2 bg-blue-100 text-blue-700 border border-blue-600 font-semibold rounded-lg text-sm hover:bg-blue-200"
                disabled={saving}
                title='Save'
              >
                Save
              </button>
              <DeleteButton
                style={`text-red-600 hover:text-red-800 px-3 py-2`}
                onDelete={() => deleteSetting(setting.id)}
              />
            </div>
          </div>
        );
    }
  }, [getSettingValue, toBool, toNumber, handleInputChange, handleToggleChange, saving, deleteSetting, updateIndividualSetting]);

  // Category titles mapping
  const categoryTitles = {
    general: 'Company Information',
    processing: 'Payroll Parameters', 
    tax: 'Tax Settings',
    allowances: 'Allowances',
    pdf: 'Payslip Settings',
    email: 'Email Settings',
    approval: 'Approval Settings'
  };

  // if (loading && settings.length === 0) {
  //   return (
  //     <PayrollLayout title="Payroll Settings">
  //       <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl">
  //         <div className="flex items-center justify-center h-64">
  //           <div className="text-blue-600 dark:text-blue-400">Loading payroll settings...</div>
  //         </div>
  //       </div>
  //     </PayrollLayout>
  //   );
  // }

  return (
    <div className="flex flex-col gap-y-6 font-poppins bg-white p-4 mt-1 mx-1">
      
      {/* Notification */}
      {notification && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
          onClose={() => setNotification(null)} 
        />
      )}

      {/* Header with Create Button */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-3xl font-semibold text-gray-900">Payroll Settings</h1>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-blue-700 w-full sm:w-auto">
          + Create New Setting
        </button>
      </div>

      {/* Create Setting Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black/60 flex justify-center items-end md:items-center z-[1001] backdrop-blur-md transition-all duration-300"
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-md w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()} // Prevent modal from closing when clicking inside
          >
            
            {/* <h3 className="text-lg font-semibold dark:text-white p-6 pb-2 mb-4 border-b">
              Create New Setting
            </h3> */}

            {/* Header */}
            <div className="relative">
              <div className="absolute inset-0"></div>
              <div className="relative flex items-center justify-between p-3 ">
                <div className="flex items-center space-x-3">
                  <div className="w-1 h-6 rounded-full"></div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                    Create New Setting
                  </h2>
                </div>
                
                {/* Enhanced close button */}
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  aria-label="Close modal"
                >
                  <X 
                    size={20} 
                    className="text-gray-600 transition-colors duration-200" 
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                </button>
              </div>
            </div>

            {/* Scrollable Form */}
            <div className="overflow-y-auto px-6 pb-6 flex-1 mt-4">
              <form onSubmit={handleCreateSetting}>

                <div className="grid grid-cols-2 gap-4">
                  {/* Setting Key */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-1">
                      Setting Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newSetting.setting_key}
                      onChange={(e) =>
                        handleNewSettingChange("setting_key", e.target.value)
                      }
                      className="w-full border dark:border-gray-700 dark:bg-gray-700 dark:text-white px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., basic_salary"
                      required
                    />
                  </div>

                  {/* Setting Name */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-1">
                      Setting Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newSetting.setting_name}
                      onChange={(e) =>
                        handleNewSettingChange("setting_name", e.target.value)
                      }
                      className="w-full border dark:border-gray-700 dark:bg-gray-700 dark:text-white px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Basic Salary"
                      required
                    />
                  </div>

                  {/* Data Type */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-1">
                      Data Type
                    </label>
                    <select
                      value={newSetting.data_type}
                      onChange={(e) =>
                        handleNewSettingChange("data_type", e.target.value)
                      }
                      className="w-full border dark:border-gray-700 dark:bg-gray-700 dark:text-white px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium dark:text-white mb-1">
                      Category
                    </label>
                    <select
                      value={newSetting.category}
                      onChange={(e) =>
                        handleNewSettingChange("category", e.target.value)
                      }
                      className="w-full border dark:border-gray-700 dark:bg-gray-700 dark:text-white px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option value="general">General</option>
                      <option value="allowances">Allowances</option>
                      <option value="tax">Tax</option>
                      <option value="processing">Processing</option>
                      <option value="pdf">PDF</option>
                      <option value="email">Email</option>
                      <option value="approval">Approval</option>
                    </select>
                  </div>

                  {/* Setting Value */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium dark:text-white mb-1">
                      Setting Value <span className="text-red-500">*</span>
                    </label>
                    {newSetting.data_type === "boolean" ? (
                      <select
                        value={newSetting.setting_value}
                        onChange={(e) =>
                          handleNewSettingChange("setting_value", e.target.value)
                        }
                        className="w-full border dark:border-gray-700 dark:bg-gray-700 dark:text-white px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        required
                        >
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    ) : (
                      <input
                        type={newSetting.data_type === "number" ? "number" : "text"}
                        value={newSetting.setting_value}
                        onChange={(e) =>
                          handleNewSettingChange("setting_value", e.target.value)
                        }
                        className="w-full border dark:border-gray-700 dark:bg-gray-700 dark:text-white px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter setting value"
                        required
                      />
                    )}
                  </div>

                  {/* Description */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium dark:text-white mb-1">
                      Description
                    </label>
                    <textarea
                      value={newSetting.description}
                      onChange={(e) =>
                        handleNewSettingChange("description", e.target.value)
                      }
                      className="w-full border dark:border-gray-700 dark:bg-gray-700 dark:text-white px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder="Enter setting description"
                    />
                  </div>

                  {/* Is Editable */}
                  <div className="col-span-2 flex items-center">
                    <input
                      type="checkbox"
                      id="is_editable"
                      checked={newSetting.is_editable}
                      onChange={(e) =>
                        handleNewSettingChange("is_editable", e.target.checked)
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"/>
                    <label htmlFor="is_editable" className="ml-2 text-sm dark:text-white">
                      Is Editable
                    </label>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end items-center gap-3 mt-4 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-7 py-2 text-black hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-7 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700">
                    Create Setting
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
            selectedCategory === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}>
          All Categories
        </button>
        {availableCategories.map(category => (
          <button
            key={category}
            onClick={() => handleCategoryChange(category)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === category
                ? 'bg-blue-600 text-white'
                : 'border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            {categoryTitles[category] || category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Settings Sections */}
      {selectedCategory === 'all' ? (
        Object.entries(settingsByCategory).map(([category, categorySettings]) => (
          <section key={category}>
            <h3 className="text-lg font-semibold dark:text-white mb-4">
              {categoryTitles[category] || category.charAt(0).toUpperCase() + category.slice(1)}
            </h3>
            
            <div className="space-y-4">
              {categorySettings.map(setting => (
                <div key={setting.id} className={setting.data_type === 'boolean' ? '' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
                  {setting.data_type === 'boolean' ? (
                    renderField(setting)
                  ) : (
                    <div>
                      {renderField(setting)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))
      ) : (
        <section>
          <h3 className="text-lg font-semibold dark:text-white mb-4">
            {categoryTitles[selectedCategory] || selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
          </h3>
          
          <div className="space-y-4">
            {filteredSettings.map(setting => (
              <div key={setting.id} className={setting.data_type === 'boolean' ? '' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
                {setting.data_type === 'boolean' ? (
                  renderField(setting)
                ) : (
                  <div>
                    {renderField(setting)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end pt-6">
        
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            disabled={saving || !hasChanges}
            className="px-7 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            Reset
          </button>
          
          <button
            onClick={saveSettings}
            disabled={saving || !hasChanges}
            className="px-7 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Bulk Update...
              </>
            ) : (
              'Bulk Update Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}